import type { ItemFieldRow } from './item-field-reader';
import { readInventoryField, readItemUuidSlot } from './inventory-raw-readers';
import { createFormulaReadContext } from './formula-read-context';
import type { FormulaReadDependencies } from './formula-read-context';
import { createInventoryReadContext } from './inventory-read-context';
import { createInventoryThinScalarReader } from './inventory-thin-scalar-reader';
import { createCatalogDefaultProviderReader } from './provider-catalog-order';
import { readNodeItemId } from './layer-cell-readers';
import { appendCompiledLayerInPlace, appendPhpCompileArray } from './status-row-construction';
import { readAbilityEntryId, readRequiredAbilityLevel, containsAbilityTriggerKey, borrowWeaponInventoryAttackLayers } from './inventory-ability-grant-helpers';
import type { InventoryAbilityGrantReaders } from './inventory-ability-grant-helpers';
import { phpJsonInt, phpJsonString, phpJsonTruthy } from './php-json-values';
import { integerCompileResult } from './integer-compute-slots';
import { PilotedObjectUnavailable } from './piloted-object';
import { FromBaseInputMissing, FromBaseStageMissing } from './from-base-context';

const arrayLike = (value: unknown): value is ItemFieldRow => value !== null && typeof value === 'object';
const own = (value: unknown, key: string): unknown => arrayLike(value) && Object.prototype.propertyIsEnumerable.call(value, key)
    ? (value as Readonly<Record<string, unknown>>)[key] : undefined;
const validUuid = (value: unknown): value is string => typeof value === 'string'
    && /^[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}(?:\n)?(?![\s\S])/i.test(value);
function row(value: unknown): ItemFieldRow {
    if (!arrayLike(value)) throw new TypeError('Inventory grant requires a PHP array');
    return value;
}
export class InventoryGrantArrayWriteError extends Error {}
function appendRoot(host: Record<string, unknown>, key: string, value: unknown): void {
    const old = own(host, key);
    if (old != null && old !== false && !arrayLike(old)) throw new InventoryGrantArrayWriteError('Cannot append an inventory grant to a PHP scalar');
    Object.defineProperty(host, key, {value: appendPhpCompileArray(arrayLike(old) ? old : [], value, true), enumerable: true, writable: true, configurable: true});
}
export interface InventoryAbilityGrantMetadata {
    dynamicLayers: readonly string[];
    inventoryAbilityTypes: readonly string[];
    computeOwnedLayers: readonly string[];
    redundancyCandidates: readonly string[];
}
export interface InventoryAbilityGrantStageReaders extends InventoryAbilityGrantReaders {
    inventoryGrantSource?: (inventory: ItemFieldRow) => boolean;
    hasLayer: (row: ItemFieldRow, name: string) => boolean;
    thinScalar: (row: ItemFieldRow, name: string) => unknown;
    queryFullItem: (id: string, suppressMissing: boolean) => unknown;
    /** Exact native condition result for this evolving host. Unavailable must throw. */
    conditions: (ability: ItemFieldRow, host: ItemFieldRow, template: false, deferSkill: true) => boolean;
    /** Exact native nested-redundancy result, including its external reads. */
    redundant: (host: ItemFieldRow, inventory: ItemFieldRow, layer: string) => boolean;
    resolvePilotedObjectId: (host: ItemFieldRow) => string | null;
    queryPilotedObject: (id: string) => unknown;
}
function grantEntry(id: unknown, ability: ItemFieldRow, inventory: ItemFieldRow, builtFrom: string, readers: InventoryAbilityGrantStageReaders): Record<string, unknown> {
    return {ability_id: id, item_name: readers.itemField(ability, 'item_name') ?? 'N/A',
        source: readInventoryField(inventory, 'inventory_id'), inventory_source: 1,
        requires_level: readRequiredAbilityLevel(inventory, readers), item_arr: ability, built_from: builtFrom,
        consume_nested_unit_per_use: readers.hasLayer(inventory, 'consumes_unit_per_use')};
}
/** A held grant reference must resolve before a caller can publish the fold. */
function grantDefinition(id: string, readers: InventoryAbilityGrantStageReaders): ItemFieldRow {
    let definition: unknown;
    try { definition = readers.queryFullItem(id, true); }
    catch (error) {
        if (!(error instanceof FromBaseInputMissing)) throw error;
        throw new FromBaseInputMissing('enchantgrants31:grant-definition-not-held', id);
    }
    if (!arrayLike(definition) || Object.keys(definition).length === 0)
        throw new FromBaseInputMissing('enchantgrants31:grant-definition-not-held', id);
    return definition;
}
/** Native equipped/flagged and enchantment grant loops. Integer layers retain
 * their guard/query ordering but are not flat-merged into creature stats. */
export function compileInventoryAbilityGrants(input: ItemFieldRow, metadata: InventoryAbilityGrantMetadata, readers: InventoryAbilityGrantStageReaders): ItemFieldRow {
    const host = Object.fromEntries(Object.entries(row(input)));
    if (!readers.hasLayer(host, 'inventory')) return integerCompileResult(host);
    for (const value of Object.values(readers.layerEntries(host, 'inventory'))) {
        const inventory = row(value);
        if (readers.inventoryGrantSource && !readers.inventoryGrantSource(inventory)) continue;
        if (phpJsonInt(readInventoryField(inventory, 'inventory_private') ?? 0) === 2) continue;
        if (readItemUuidSlot(readInventoryField(inventory, 'equipped') ?? '') === ''
            && !phpJsonTruthy(readers.thinScalar(inventory, 'acquire_abilities_while_unequipped'))) continue;
        for (const layer of new Set([...metadata.dynamicLayers, ...metadata.inventoryAbilityTypes])) {
            if (!readers.hasLayer(inventory, layer) || metadata.computeOwnedLayers.includes(layer)) continue;
            if (metadata.redundancyCandidates.includes(layer) && readers.redundant(host, inventory, layer)) continue;
            for (const entry of Object.values(readers.layerEntries(inventory, layer))) {
                let id: unknown = entry, status: unknown = null;
                if (arrayLike(entry) && own(entry, 'built_from_item_type') === 'status_effect') {
                    status = phpJsonTruthy(own(entry, 'built_from_id')) ? own(entry, 'built_from_id')
                        : phpJsonTruthy(own(entry, 'source')) ? own(entry, 'source') : null;
                    id = readAbilityEntryId(entry, readers);
                }
                if (!metadata.inventoryAbilityTypes.includes(layer) || !validUuid(id)) continue;
                let ability: ItemFieldRow = grantDefinition(id, readers);
                if (layer === 'attack_ability' || layer === 'opportunity_attack_ability') ability = borrowWeaponInventoryAttackLayers(ability, inventory, readers);
                if (phpJsonTruthy(readers.layerValue(ability, 'draw_sheathed_weapon_for_attack_ability'))) {
                    ability = {...Object.fromEntries(Object.entries(ability)), _draw_bound_inventory_id: [readInventoryField(inventory, 'inventory_id')]};
                }
                if (!readers.conditions(ability, host, false, true)) {
                    if (containsAbilityTriggerKey(ability) || phpJsonTruthy(readers.layerValue(ability, 'draw_sheathed_weapon_for_attack_ability'))) continue;
                }
                const grant = grantEntry(id, ability, inventory, 'inventory', readers);
                if (status !== null) grant.granted_by_status_id = status;
                appendRoot(host, layer, grant);
            }
        }
        const enchanted = own(own(own(inventory, 'computed'), 'enchantment'), 'abilities');
        for (const [layer, entries] of arrayLike(enchanted) ? Object.entries(enchanted) : []) {
            if (!arrayLike(entries) || !metadata.inventoryAbilityTypes.includes(layer)) continue;
            for (const entry of Object.values(entries)) {
                let definition = own(entry, 'item_arr');
                if (!arrayLike(definition) && arrayLike(entry) && validUuid(phpJsonString(own(entry, 'item_id')))) {
                    const partId = phpJsonString(own(entry, 'item_id'));
                    definition = grantDefinition(partId, readers);
                }
                if (!arrayLike(definition)) continue;
                let ability: ItemFieldRow = definition;
                const id = readers.itemField(ability, 'item_id');
                if (!validUuid(phpJsonString(id))) continue;
                if (layer === 'attack_ability' || layer === 'opportunity_attack_ability') ability = borrowWeaponInventoryAttackLayers(ability, inventory, readers);
                if (!readers.conditions(ability, host, false, true) && containsAbilityTriggerKey(ability)) continue;
                const grant = grantEntry(id, ability, inventory, 'enchantment', readers);
                const consume = grant.consume_nested_unit_per_use; delete grant.consume_nested_unit_per_use;
                grant.enchantment_template_id = own(entry, 'source') ?? null; grant.consume_nested_unit_per_use = consume;
                appendRoot(host, layer, grant);
            }
        }
    }
    return integerCompileResult(host);
}
export function compilePilotedInventoryAbilityGrants(input: ItemFieldRow, metadata: InventoryAbilityGrantMetadata, readers: InventoryAbilityGrantStageReaders,
    normalize = true): ItemFieldRow {
    const host = Object.fromEntries(Object.entries(row(input)));
    const finish = () => normalize ? integerCompileResult(host) : host;
    const id = readers.resolvePilotedObjectId(host);
    if (id === null) return finish();
    let object: unknown;
    try { object = readers.queryPilotedObject(id); }
    catch (error) {
        if (!(error instanceof PilotedObjectUnavailable || error instanceof FromBaseInputMissing || error instanceof FromBaseStageMissing)) throw error;
        host._piloted_ability_refusal = error instanceof PilotedObjectUnavailable ? error.message : `piloted-abilities:vehicle-not-held:${id}:${error.message}`;
        return finish();
    }
    // Operated objects only: war_machine, or an item carrying operable_object (twin of ft_item_is_operable_object()).
    if (!arrayLike(object) || Object.keys(object).length === 0
        || ((readers.itemField(object, 'item_type') ?? '') !== 'war_machine' && Number(phpJsonString(readers.layerValue(object, 'operable_object')) ?? 0) !== 1)) return finish();
    for (const layer of metadata.inventoryAbilityTypes) {
        if (!readers.hasLayer(object, layer)) continue;
        for (const entry of Object.values(readers.layerEntries(object, layer))) {
            const abilityId = readAbilityEntryId(entry, readers);
            if (!validUuid(abilityId)) continue;
            if (layer === 'passive_ability') {
                host._piloted_ability_refusal = `piloted-abilities:passive-control:${abilityId}`;
                continue;
            }
            try {
                const ability = readers.queryFullItem(abilityId, true);
                if (!arrayLike(ability)) {
                    host._piloted_ability_refusal = `piloted-abilities:control-not-held:${abilityId}`;
                    continue;
                }
                if (!readers.conditions(ability, host, false, true) && containsAbilityTriggerKey(ability)) continue;
                const grant = {ability_id: abilityId, item_name: readers.itemField(ability, 'item_name') ?? 'N/A',
                    source: id, inventory_source: 1, requires_level: readRequiredAbilityLevel(object, readers),
                    item_arr: ability, built_from: 'piloted', consume_nested_unit_per_use: false};
                const existing = own(host, layer);
                if (Array.isArray(existing)) host[layer] = [...existing, grant];
                else appendRoot(host, layer, grant);
            } catch (error) {
                if (!(error instanceof FromBaseInputMissing || error instanceof FromBaseStageMissing)) throw error;
                host._piloted_ability_refusal = `piloted-abilities:control-not-held:${abilityId}:${error.message}`;
            }
        }
    }
    return finish();
}
export function compileSheathedInventoryAbilityGrants(input: ItemFieldRow, readers: InventoryAbilityGrantStageReaders): ItemFieldRow {
    const host = Object.fromEntries(Object.entries(row(input)));
    if (!readers.hasLayer(host, 'inventory') || !arrayLike(own(host, 'inventory'))) return integerCompileResult(host);
    for (const value of Object.values(readers.layerEntries(host, 'inventory'))) {
        const inventory = row(value);
        if (readers.inventoryGrantSource && !readers.inventoryGrantSource(inventory)) continue;
        if (readItemUuidSlot(readInventoryField(inventory, 'equipped') ?? '') !== ''
            || phpJsonInt(readInventoryField(inventory, 'inventory_private') ?? 0) === 2
            || (readers.itemField(inventory, 'item_type') ?? '') !== 'weapon'
            || !phpJsonTruthy(readers.layerEntries(inventory, 'attack_ability'))) continue;
        for (const id of Object.values(readers.layerEntries(inventory, 'attack_ability'))) {
            if (!validUuid(id)) continue;
            const definition = readers.queryFullItem(id, true);
            if (!arrayLike(definition)) continue;
            let ability: ItemFieldRow = borrowWeaponInventoryAttackLayers(definition, inventory, readers);
            if (!phpJsonTruthy(readers.layerValue(ability, 'draw_sheathed_weapon_for_attack_ability'))) continue;
            ability = {...Object.fromEntries(Object.entries(ability)), _draw_bound_inventory_id: [readInventoryField(inventory, 'inventory_id')]};
            if (!readers.conditions(ability, host, false, true)) continue;
            appendCompiledLayerInPlace(host, 'attack_ability', grantEntry(id, ability, inventory, 'inventory_sheathed_weapon', readers));
        }
    }
    return integerCompileResult(host);
}
export interface InventoryAbilityGrantDependencies extends FormulaReadDependencies, Pick<InventoryAbilityGrantStageReaders,
    'queryFullItem' | 'conditions' | 'redundant' | 'resolvePilotedObjectId' | 'queryPilotedObject' | 'inventoryGrantSource'> {
    metadata: InventoryAbilityGrantMetadata;
    templateValueMemo?: Map<string, Map<string, unknown>>;
}
export function createInventoryAbilityGrantCompiler(deps: InventoryAbilityGrantDependencies) {
    const emit = (event: readonly unknown[]) => deps.trace?.(event);
    const inventoryContext = deps.inventoryContext ?? createInventoryReadContext(id => { emit(['inventory', id]); return deps.resolveInventoryInstance(id); });
    const common = {...deps, inventoryContext}, context = createFormulaReadContext(common);
    const nodeItemId = (value: ItemFieldRow) => { const id = readNodeItemId(value); emit(['node', id]); return id; };
    const provider = createCatalogDefaultProviderReader({catalogRows: deps.catalogRows, nodeItemId,
        resolveCarrierRef: id => { emit(['reference', id]); return deps.resolveCarrierRef(id); }});
    const thin = createInventoryThinScalarReader({inventoryContext, nodeItemId,
        providers: function* (value) { emit(['providers']); yield* provider.providers(value); },
        queryTemplate: id => deps.queryFullItem(id, true), ...(deps.templateValueMemo ? {templateValueMemo: deps.templateValueMemo} : {})});
    const readers: InventoryAbilityGrantStageReaders = {...context,
        inventoryGrantSource: deps.inventoryGrantSource,
        resolveCarrierRef: id => { emit(['reference', id]); return deps.resolveCarrierRef(id); },
        hasLayer: (value, name) => context.layerValue(value, name) !== null || phpJsonTruthy(context.layerEntries(value, name)),
        thinScalar: thin.read, queryFullItem: deps.queryFullItem, conditions: deps.conditions, redundant: deps.redundant,
        resolvePilotedObjectId: deps.resolvePilotedObjectId, queryPilotedObject: deps.queryPilotedObject};
    return {
        inventory: (host: ItemFieldRow) => compileInventoryAbilityGrants(host, deps.metadata, readers),
        piloted: (host: ItemFieldRow, normalize = true) => compilePilotedInventoryAbilityGrants(host, deps.metadata, readers, normalize),
        sheathed: (host: ItemFieldRow) => compileSheathedInventoryAbilityGrants(host, readers),
        all: (host: ItemFieldRow) => compileSheathedInventoryAbilityGrants(
            compilePilotedInventoryAbilityGrants(compileInventoryAbilityGrants(host, deps.metadata, readers), deps.metadata, readers), readers),
    };
}
